import type { CreateDeviceInput, UpdateDeviceInput } from "@repo/api";
import { locationIdSchema } from "@repo/api/schemas";
import { type DeviceDto, useLocations } from "@repo/data-store";
import { mintEntityId } from "@repo/domain";
import { useMemo } from "react";
import { z } from "zod";
import { useAppForm, withForm } from "@/shared/forms/app-form";

// === Schema & Types ===

/** The picker's "no location" answer. Not a location id, so it never reaches the domain. */
export const NO_LOCATION = "none";

/**
 * One physical unit's own fields.
 *
 * The DEFINITION is not here: a unit is added from its definition's page, so
 * the definition IS the type and asking again invites the two to disagree.
 *
 * `deviceId` is ABSENT ON A ROW THAT HAS NEVER BEEN WRITTEN, and that absence
 * is the only thing separating a register from an update at save time. It is
 * not user input — the form carries it so the row it belongs to can be
 * recognised after the list has been reordered or had earlier rows removed.
 *
 * LOCATION IS OPTIONAL, and used not to be. It was required by the form while
 * the domain called an unsited unit new stock rather than an invalid one, and
 * the input builder mapped the picker's "none" to `undefined` — so the schema
 * and the builder contradicted each other, and the contradiction was invisible:
 * the save simply returned, sending no request at all. The domain wins.
 */
export const deviceInstanceSchema = z.object({
  deviceId: z.string().optional(),
  name: z.string().min(1, "Name is required"),
  udi: z.string().min(1, "Unique device identifier is required"),
  locationId: z.string(),
});

export type DeviceInstanceValues = z.infer<typeof deviceInstanceSchema>;

export const EMPTY_DEVICE_INSTANCE: DeviceInstanceValues = {
  name: "",
  udi: "",
  locationId: NO_LOCATION,
};

/**
 * The whole instances list, staged. A SEPARATE FORM FROM THE DEFINITION'S
 * CONTENT, not another field on it: `deviceDefinitionValuesToInput` builds a
 * define/revise payload, and an instance must never travel in one — a unit is
 * its own aggregate and adding one is not a new version of the model.
 */
export const deviceInstancesFormSchema = z.object({
  instances: z.array(deviceInstanceSchema),
});

export type DeviceInstancesFormValues = z.infer<typeof deviceInstancesFormSchema>;

export const EMPTY_DEVICE_INSTANCES: DeviceInstancesFormValues = { instances: [] };

// === Converters ===

export function deviceInstanceToValues(device: DeviceDto): DeviceInstanceValues {
  return {
    deviceId: device.deviceId,
    name: device.primaryName ?? "",
    udi: device.distinctIdentifier ?? "",
    locationId: device.locationId ?? NO_LOCATION,
  };
}

export function deviceInstancesToDefaults(devices: readonly DeviceDto[]): DeviceInstancesFormValues {
  return { instances: devices.map(deviceInstanceToValues) };
}

function toLocationId(locationId: string): UpdateDeviceInput["locationId"] {
  return locationId === NO_LOCATION ? undefined : locationIdSchema.parse(locationId);
}

/** The values of one draft row, assembled into a register intent for its definition. */
export function buildRegisterDeviceInput(values: DeviceInstanceValues, definitionUrl: string): CreateDeviceInput {
  return {
    id: mintEntityId(),
    scheduleId: mintEntityId(),
    definitionUrl,
    status: "active",
    label: values.name,
    distinctIdentifier: values.udi,
    locationId: toLocationId(values.locationId),
  };
}

/**
 * What actually changed on an existing unit, and nothing else — `null` when
 * that is nothing. Sending only the edited fields keeps one row's write from
 * rewriting values another writer moved in the meantime.
 */
export function buildUpdateDeviceInput(
  id: UpdateDeviceInput["id"],
  values: DeviceInstanceValues,
  previous: DeviceInstanceValues,
): UpdateDeviceInput | null {
  const changes: UpdateDeviceInput = { id };
  if (values.name !== previous.name) changes.label = values.name;
  if (values.udi !== previous.udi) changes.distinctIdentifier = values.udi || undefined;
  if (values.locationId !== previous.locationId) changes.locationId = toLocationId(values.locationId);
  return Object.keys(changes).length > 1 ? changes : null;
}

/** One unit's pending write, named so a partial failure can say which row failed. */
export type InstanceWrite =
  | { kind: "register"; label: string; input: CreateDeviceInput }
  | { kind: "update"; label: string; input: UpdateDeviceInput }
  | { kind: "deregister"; label: string; id: UpdateDeviceInput["id"] };

/**
 * Every write the staged list implies, against the units it was opened with.
 *
 * THE BASELINE IS THE UNITS THE FORM LOADED, not the live query's current
 * answer: a row is diffed against what the editor was shown, so a unit another
 * writer moved in the meantime is not silently reverted by a field this editor
 * never touched.
 *
 * A row with no `deviceId` registers, a known one updates only its changed
 * fields, and a baseline unit whose row is gone deregisters. An unchanged row
 * yields nothing, which is what keeps Save on an untouched list silent.
 */
export function buildInstanceWrites(
  baseline: readonly DeviceDto[],
  values: readonly DeviceInstanceValues[],
  definitionUrl: string,
): InstanceWrite[] {
  const writes: InstanceWrite[] = [];
  const previousById = new Map(baseline.map((device) => [String(device.deviceId), device]));
  const keptIds = new Set<string>();

  for (const row of values) {
    if (!row.deviceId) {
      writes.push({ kind: "register", label: row.name, input: buildRegisterDeviceInput(row, definitionUrl) });
      continue;
    }
    keptIds.add(row.deviceId);
    const previous = previousById.get(row.deviceId);
    // A row citing an id the baseline never had is not an update to anything.
    // Registering it instead would mint a second unit for the same equipment,
    // so it is dropped: the row came from a list this editor did not load.
    if (!previous) continue;
    const input = buildUpdateDeviceInput(previous.deviceId, row, deviceInstanceToValues(previous));
    if (input) writes.push({ kind: "update", label: row.name, input });
  }

  for (const device of baseline) {
    if (keptIds.has(String(device.deviceId))) continue;
    writes.push({
      kind: "deregister",
      label: device.primaryName ?? "Unnamed instance",
      id: device.deviceId,
    });
  }

  return writes;
}

// === Hooks ===

export function useDeviceInstanceFormData() {
  const { locations } = useLocations();

  // `displayPath` is resolved by the location view, so the option label is a
  // plain string here and the picker can search it.
  const locationOptions = useMemo(
    () => [
      { value: NO_LOCATION, label: "None" },
      ...locations.map((loc) => ({ value: loc.locationId, label: loc.displayPath })),
    ],
    [locations],
  );

  return { locationOptions };
}

export function useDeviceInstancesForm(devices: readonly DeviceDto[]) {
  return useAppForm({
    defaultValues: deviceInstancesToDefaults(devices),
    validators: { onDynamic: deviceInstancesFormSchema },
    onSubmit: () => {},
  });
}

export type DeviceInstancesForm = ReturnType<typeof useDeviceInstancesForm>;

export { useAppForm, withForm };
